import { i18n } from 'app/i18n/i18n';
import Cookies from 'js-cookie';
import { INFORMATION_PROMPT_GRACE_TIME } from './cookies';

export const formatDate = (dateTimeString: string): string => {
  const date = dateTimeString.split(' ')[0];
  const time = dateTimeString.split(' ')[1];
  const dateFragments = date.split('/');
  const localDateTime = `${dateFragments[2]}.${dateFragments[1]}.${dateFragments[0]}`;
  return `${time !== undefined ? time : ''} ${localDateTime}`;
};

export const hasCookieGraceTimeExpired = (cookie: string) => {
  const graceTime = Cookies.get(cookie);
  if (!graceTime) {
    return true;
  }
  if (Date.now() < Number.parseInt(graceTime)) {
    return false;
  }
  Cookies.remove(INFORMATION_PROMPT_GRACE_TIME);
  return true;
};

/*
 * Converts 2021-10-03T10:57:13Z to 10 Oktober
 */
export const iso8601ToDayAndFullMonth = (iso8601String: string): string => {
  const date = new Date(iso8601String);

  // AKW-2803: Make locale dynamic
  return `${date.toLocaleString('no-nb', { day: 'numeric' })} ${date.toLocaleString('no-nb', { month: 'long' })}`;
};

/*
 * Converts 2021-10-03T10:57:13Z to Oktober 2021
 */
export const iso8601ToFullMonthAndYear = (iso8601String: string) => {
  const date = new Date(iso8601String);
  const month = date.toLocaleString('no-nb', { month: 'long' });
  return isValidDate(date) ? `${month.charAt(0).toUpperCase()}${month.slice(1)} ${date.getFullYear()}` : iso8601String;
};

/*
 * Converts 2021-10-03T10:57:13Z to 10. oktober 2021
 */
export const iso8601ToDayFullMonthAndYear = (iso8601String: string) => {
  const date = new Date(iso8601String);
  const day = date.toLocaleString('no-nb', { day: 'numeric' });
  const month = date.toLocaleString('no-nb', { month: 'long' });
  return isValidDate(date) ? `${day} ${month} ${date.getFullYear()}` : iso8601String;
};

/*
 * Converts 2021-10-03T10:57:13Z to 3. oktober 2021, 12:57
 */
export const iso8601ToDayFullMonthYearHourMinute = (iso8601String: string) => {
  const date = new Date(iso8601String);
  // AKW-2803: Make locale dynamic
  return `${date.toLocaleString('no-nb', {
    month: 'long',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    year: 'numeric',
  })}`;
};

/*
 * Converts 2021-10-03T10:57:13Z to 03.10.21
 */
export const iso8601ToShortDate = (iso8601String: string) => {
  const date = new Date(iso8601String);
  return `${date.toLocaleString('no-nb', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit',
  })}`;
};

/*
 * Converts 2021-10-03T10:07:13Z to 10:07
 */
export const iso8601ToTime = (iso8601String: string) => {
  const date = new Date(iso8601String);
  return `${('0' + date.getHours()).slice(-2)}:${('0' + date.getMinutes()).slice(-2)}`;
};

export const secondsToDuration = (seconds: number) => new Date(seconds * 1000).toISOString().slice(11, 19);

export const formatDateOrDateRange = (startDateIso8601String?: string, endDateIso8601String?: string) => {
  if (!startDateIso8601String) {
    return null;
  }

  let result;

  if (endDateIso8601String) {
    const startDate = new Date(startDateIso8601String);
    const endDate = new Date(endDateIso8601String);

    result =
      startDate.getMonth() === endDate.getMonth()
        ? `${startDate.getDate()}. - ${iso8601ToDayAndFullMonth(endDateIso8601String)}`
        : `${iso8601ToDayAndFullMonth(startDateIso8601String)} - ${iso8601ToDayAndFullMonth(endDateIso8601String)}`;
  } else {
    result = iso8601ToDayAndFullMonth(startDateIso8601String);
  }

  return result;
};

export const getPreviousXPeriodsFrom = (x?: number, from?: Date): string[] => {
  const now = from ?? new Date();
  const periods = [now.toISOString().substring(0, 7)];
  for (let i = 1; i < (x || 0); i++) {
    const period = new Date(now.setMonth(now.getMonth() - 1));
    periods.push(period.toISOString().substring(0, 7));
  }

  return periods;
};

export const minutesToHoursAndMinutes = (min: number) => {
  const days = Math.floor(min / (24 * 60));

  const remainingMinutes = min - days * 24 * 60;

  const hours = Math.floor(remainingMinutes / 60);

  const minutes = remainingMinutes - hours * 60;

  return formatDuration(days, hours, minutes);
};

// Example usage:
// formatDuration(3, 12, 30); // "3 dager, 12 timer og 30 minutter"
// formatDuration(3, 12, 0);  // "3 dager og 12 timer"
// formatDuration(1, 0, 30);  // "1 dag og 30 minutter"
// formatDuration(3, 0, 0);   // "3 dager"
// formatDuration(0, 12, 30); // "12 timer og 30 minutter"
// formatDuration(0, 12, 0);  // "12 timer"
// formatDuration(0, 0, 30);  // "30 minutter"
function formatDuration(days: number, hours: number, minutes: number) {
  let result = '';

  const i18and = i18n.t('utils.minutesToHoursAndMinutes.and');

  if (days > 0) {
    result += `${days} ${i18n.t('utils.minutesToHoursAndMinutes.days', { count: days })}`;
  }

  if (hours > 0) {
    if (result) {
      result += minutes > 0 ? ', ' : ` ${i18and} `;
    }
    result += `${hours} ${i18n.t('utils.minutesToHoursAndMinutes.hours', { count: hours })}`;
  }

  if (minutes > 0) {
    if (result) {
      result += ` ${i18and} `;
    }
    result += `${minutes} ${i18n.t('utils.minutesToHoursAndMinutes.minutes', { count: minutes })}`;
  }

  if (!result) {
    result = `${minutes} ${i18n.t('utils.minutesToHoursAndMinutes.minutes', { count: minutes })}`;
  }

  return result;
}

const isValidDate = (date: unknown) => date instanceof Date && !isNaN(date.getTime());

export const dueDateExceeded = (possiblyFutureDate: string) => {
  const date = new Date(possiblyFutureDate).getTime();
  const now = new Date().getTime();
  return now > date;
};
